<template>
  <div>
    <fieldset>
      <legend>
        Invoices
        <button
          type="button"
          class="btn btn-primary btn-sm float-right"
          @click="newInvoice"
          v-if="hasPermission('invoices.create')"
        >
          New Invoice
        </button>
      </legend>
    </fieldset>

    <div class="row">
      <div
        class="col-md-6 col-lg-4"
        :key="index"
        v-for="(invoice, index) in recovery.invoices"
      >
        <div class="card mb-3 h-100">
          <div class="card-body text-center">
            <small class="text-muted">Invoice Number</small><br />
            <p class="mb-0">{{ invoice.formatted_invoice_number }}</p>
            <small class="text-muted">Heads Of Claim</small><br />
            <p class="mb-0">{{ rateGroupsLabel(invoice) }}</p>
            <small class="text-muted">Generated At</small><br />
            <p class="mb-0">{{ invoice.created_at | date }}</p>
            <small class="text-muted">Invoice Age</small><br />
            <p class="mb-0">
              {{ invoiceAge(invoice) }} day<template
                v-if="invoiceAge(invoice) !== 1"
                >s
              </template>
            </p>
            <small class="text-muted">Invoiced To</small><br />
            <p class="mb-0">{{ invoice.recipient?.label }}</p>
            <small class="text-muted">Insurer</small><br />
            <p class="mb-0">{{ invoice.insurer?.name }}</p>
            <small class="text-muted">Paid</small><br />
            <p class="mb-3">{{ invoice.paid_at | date }}</p>

            <form-button
              :block="true"
              :indicator="downloading === invoice.id"
              @clicked="download(invoice)"
              class="mt-1"
            >
              Download Proforma Invoice
            </form-button>
            <form-button
              :block="true"
              :indicator="markingAsPaid === invoice.id"
              @clicked="markAsPaid(invoice)"
              class="mt-1"
              v-if="!invoice.paid_at && hasPermission('invoices.update')"
            >
              Mark As Paid
            </form-button>
            <form-button
              :block="true"
              :indicator="downloadingTrueInvoice === invoice.id"
              @clicked="downloadTrueInvoice(invoice)"
              class="mt-1"
            >
              Download Final Invoice
            </form-button>
            <form-button
              :block="true"
              level="danger"
              :indicator="deleting === invoice.id"
              @clicked="deleteInvoice(invoice)"
              v-if="hasPermission('invoices.delete')"
              class="mt-1"
            >
              Delete
            </form-button>
          </div>
        </div>
      </div>
    </div>

    <add-invoice
      :incident="incident"
      :recovery="recovery"
      :rate-groups="rateGroups"
      :show="modals.add"
      v-on:close="closeAddInvoiceModal"
    ></add-invoice>
  </div>
</template>
<script>
import AddInvoice from '@/components/Modules/Recovery/Sections/Figures/Invoices/AddInvoice';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    recovery: {
      type: Object,
      required: true
    },
    rateGroups: {
      type: Array,
      required: true
    }
  },

  components: {
    AddInvoice
  },

  data() {
    return {
      claimRoute: null,
      invoices: [],
      deleting: null,
      downloading: null,
      downloadingTrueInvoice: null,
      markingAsPaid: null,
      generating: null,
      modals: {
        add: false
      }
    };
  },

  methods: {
    rateGroupsLabel(invoice) {
      return this.rateGroups
        .filter(group => invoice.rate_groups.includes(group.id))
        .map(group => group.label)
        .join(', ');
    },

    newInvoice() {
      this.modals.add = true;
    },

    closeAddInvoiceModal() {
      this.modals.add = false;
    },

    download(invoice) {
      this.downloading = invoice.id;

      window.Ajax.download(
        `debt-recovery/${this.incident}/invoices/${invoice.identifier}/download`
      )
        .then(() => (this.downloading = null))
        .catch(() => {
          this.downloading = null;

          window.toastify.error(
            'Error!',
            'An error occurred while downloading the invoice'
          );
        });
    },

    downloadTrueInvoice(invoice) {
      this.downloadingTrueInvoice = invoice.id;

      window.Ajax.download(
        `debt-recovery/${this.incident}/invoices/${invoice.identifier}/download-true-invoice`
      )
        .then(() => (this.downloadingTrueInvoice = null))
        .catch(() => {
          this.downloadingTrueInvoice = null;

          window.toastify.error(
            'Error!',
            'An error occurred while downloading the invoice'
          );
        });
    },

    markAsPaid(invoice) {
      window
        .swal({
          title: 'Are you sure?',
          text: `Are you sure you want to mark that invoice as paid?`,
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          this.markingAsPaid = invoice.id;

          window.Ajax.put(
            `debt-recovery/${this.incident}/invoices/${invoice.identifier}/paid`,
            {}
          )
            .then(response => {
              this.markingAsPaid = null;

              const index = this.recovery.invoices.findIndex(
                i => i.id === invoice.id
              );

              if (index > -1) {
                this.recovery.invoices[index] = response.data;
              }

              window.toastify.success(
                'Success!',
                'The invoice was updated successfully!'
              );
            })
            .catch(() => {
              this.markingAsPaid = null;

              window.toastify.error(
                'Error!',
                'An error occurred while marking the invoice as paid'
              );
            });
        });
    },

    deleteInvoice(invoice) {
      window
        .swal({
          title: 'Are you sure?',
          text: `Are you sure you want to delete that invoice?`,
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          this.deleting = invoice.id;

          window.Ajax.delete(
            `/debt-recovery/${this.incident}/invoices/${invoice.identifier}`
          )
            .then(response => {
              this.deleting = null;

              const index = this.recovery.invoices.findIndex(
                i => i.id === invoice.id
              );

              this.recovery.invoices.splice(index, 1);

              window.toastify.success(
                'Success!',
                'The invoice was deleted successfully!'
              );
            })
            .catch(() => {
              this.deleting = null;

              window.toastify.error(
                'Error!',
                'An error occurred while deleting the invoice'
              );
            });
        });
    },
    invoiceAge(invoice) {
      return (
        window.DateManager.differenceInDays(new Date(), invoice.created_at) + 1
      );
    }
  }
};
</script>
